import { Link, LinkProps, Search } from '@tanstack/react-location';
import { useRouter } from '@frontend/weave-router';
import { useInterRouter } from './inter-router';
import { SettingsRoutes } from './settings-routes';
import type { PathSegments, RouteParams } from './types';
import { useConstructSettingsLabel } from './use-construct-settings-label';

type BaseNavigateArgs<T extends SettingsRoutes> = Omit<LinkProps, 'to' | 'params'> & {
  to: T;
  children: React.ReactNode;
  search?: Search<unknown>;
  replace?: boolean;
  trackingId?: string;
};

type NavigateArgs<T extends SettingsRoutes> = PathSegments<T> extends never
  ? BaseNavigateArgs<T>
  : BaseNavigateArgs<T> & { params?: RouteParams<T> };

export const SettingsLink = <T extends SettingsRoutes>({
  children,
  onClick,
  to,
  replace,
  trackingId,
  ...rest
}: NavigateArgs<T>) => {
  const { setSettingsRouterPathHistory, setSettingsRouterPath, settingsRouterPath, shouldBlockFn } = useInterRouter([
    'setSettingsRouterPathHistory',
    'setSettingsRouterPath',
    'settingsRouterPath',
    'shouldBlockFn',
  ]);
  const constructLabel = useConstructSettingsLabel();

  const router = useRouter();
  const history = router.history;

  let newPath: string = to;

  if ('params' in rest && rest.params) {
    for (const [key, value] of Object.entries<string>(rest.params as RouteParams<T>)) {
      newPath = newPath.replace(`:${key}`, value);
    }
  }

  const params = 'params' in rest ? (rest.params as RouteParams<T>) : {};

  return (
    <Link
      onClick={(e) => {
        // If `to` is undefined but an onClick handler exists, we assume the onClick handler will manage navigation
        // and blocking logic since we don't have a valid path to create a navigation context for blocking.
        if (!to && !!onClick) {
          onClick(e);
          return;
        }

        if (shouldBlockFn) {
          const next = {
            fullPath: to,
            params,
            pathname: newPath,
            search: rest.search ?? {},
            isHandledBySettingsRouter: true,
          };
          if (
            shouldBlockFn({
              next,
              current: {
                fullPath: '',
                params: {},
                pathname: history.location.hash,
                search: history.location.search,
                isHandledBySettingsRouter: true,
              },
            })
          ) {
            e.preventDefault();
            return;
          }
        }

        if (rest.disabled) return;
        if (settingsRouterPath.path === newPath) return;
        if (newPath) {
          setSettingsRouterPathHistory({
            path: newPath,
            raw: to,
            label: constructLabel(to, params),
            search: rest.search,
            replace,
          });
          setSettingsRouterPath({ path: newPath, raw: to, search: rest.search, replace });
        }
        onClick?.(e);
      }}
      to={rest.disabled ? undefined : newPath}
      replace={replace}
      data-trackingid={trackingId}
      {...rest}
    >
      {children}
    </Link>
  );
};
